import { useQuery } from '@hooks/reactQuery';

import { getChangesMonitorUpdateString } from '@api/manageNetworkElementConfigurations';

import { parseRawConfig } from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/utils';

import { IUpdateStringResponse } from '@interfaces/networkElementConfigurations/networkElementConfigurations';

import { EUserRoles } from '@enums/common';

import { refetchOptions } from '@constants/common';
import { NETWORK_ELEMENT_CHANGES_MONITORING_CARD } from '@constants/queryKeys';

export function useQueryChangesMonitoringUpdateString({
	id,
	onSuccess,
	onError,
}: {
	id: string;
	onSuccess?: (data?: IUpdateStringResponse) => void;
	onError?: () => void;
}): {
	data?: IUpdateStringResponse;
	isUpdateStringLoading: boolean;
} {
	const {
		data,
		isFetching,
		isInitialLoading,
	} = useQuery<IUpdateStringResponse>({
		allowedRoles: [
			EUserRoles.networkOperatorCmModuleViewer,
			EUserRoles.networkOperatorCmModuleEditor,
			EUserRoles.networkOperatorCmModuleAdmin,
		],
		enabled: [!!id],
		onError,
		onSuccess,
		queryFn: async () => {
			const worker = new Worker(
				new URL('../../components/routes/AdminPanel/ManageNetworkElementConfigurations/parseWorker', import.meta.url),
				{
					type: 'module',
				},
			);

			try {
				const { updateString, validationRulesFolderId } = await getChangesMonitorUpdateString(id);

				const tree = await parseRawConfig(
					updateString,
					worker,
				);

				return {
					tree,
					updateString,
					validationRulesFolderId,
				};
			} finally {
				worker.terminate();
			}
		},
		queryFnParams: [id],
		queryKey: [NETWORK_ELEMENT_CHANGES_MONITORING_CARD, id],
		...refetchOptions,
		staleTime: 0,
		cacheTime: 0,
	});

	return {
		data,
		isUpdateStringLoading: isFetching || isInitialLoading,
	};
}
